import React, { useState } from "react";
import Modal from "../../../../components/ui/Modal";
import Button from "../../../../components/ui/Button";
import Input from "../../../../components/ui/Input";
import { Link } from "lucide-react";

const ManageSubscriptionsModal = ({ isOpen, onClose, subscriptions, onSave, editingSubscription = null }) => {
  const [activeTab, setActiveTab] = React.useState("create");
  
  // Create Tier State
  const [tierName, setTierName] = React.useState("");
  const [price, setPrice] = React.useState("");
  const [benefits, setBenefits] = React.useState("");

  // Connect Platform State
  const [selectedPlatform, setSelectedPlatform] = React.useState(null); // 'Sewasew Multimedia' | 'Sewasew SRBT'
  const [margin, setMargin] = React.useState("");
  const [platformPrice, setPlatformPrice] = React.useState(""); // Calculated final price

  const SEWASEW_BASE_MONTHLY = 90;
  const SEWASEW_BASE_YEARLY = 918;

  // Populate form when editing
  React.useEffect(() => {
    if (editingSubscription) {
      if (editingSubscription.type === "external") {
        setActiveTab("connect");
        setSelectedPlatform(editingSubscription.platform);
        setPrice(editingSubscription.priceETB?.toString() || "");
        // Reverse calculate margin if needed, or just set total price
        setPlatformPrice(editingSubscription.priceETB?.toString() || "");
        setMargin((editingSubscription.priceETB - SEWASEW_BASE_MONTHLY).toString());
      } else {
        setActiveTab("create");
        setTierName(editingSubscription.name || "");
        setPrice(editingSubscription.priceETB?.toString() || "");
        setBenefits(editingSubscription.perks?.join(", ") || editingSubscription.benefits?.join(", ") || "");
      }
    } else {
      setActiveTab("create");
      setTierName("");
      setPrice("");
      setBenefits("");
      setSelectedPlatform(null);
      setMargin("");
      setPlatformPrice("");
    }
  }, [editingSubscription, isOpen]);

  // Calculate platform price when margin changes
  React.useEffect(() => {
    if (selectedPlatform && margin) {
      const baseObj = SEWASEW_BASE_MONTHLY;
      const marginVal = parseFloat(margin) || 0;
      setPlatformPrice((baseObj + marginVal).toFixed(2));
    }
  }, [margin, selectedPlatform]);

  const handleSaveTier = () => {
    if (activeTab === "create") {
      const tierData = {
        id: editingSubscription?.id || `sub_${Date.now()}`,
        name: tierName,
        priceETB: Number(price),
        perks: benefits.split(",").map(b => b.trim()),
        benefits: benefits.split(",").map(b => b.trim()),
        subscribers: editingSubscription?.subscribers || 0,
        type: "internal",
        logoUrl: editingSubscription?.logoUrl || "https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png",
        platform: "Adnaki"
      };
      onSave(tierData);
    } else {
      // Connect Platform Save
      const tierData = {
        id: editingSubscription?.id || `sub_${Date.now()}`,
        name: `${selectedPlatform} Integration`,
        priceETB: Number(platformPrice),
        perks: [`Access to ${selectedPlatform}`, "Exclusive Content", "Supporter Badge"],
        benefits: [`Access to ${selectedPlatform}`, "Exclusive Content", "Supporter Badge"],
        subscribers: editingSubscription?.subscribers || 0,
        type: "external",
        logoUrl: "https://play-lh.googleusercontent.com/mO_U8-iRjKkYyBqjXbXb6gQxQxQxQxQxQxQxQxQxQxQxQxQxQxQxQxQxQxQxQxQ", // Placeholder for Sewasew logo
        platform: selectedPlatform
      };
      onSave(tierData);
    }
    
    // Reset form
    setTierName("");
    setPrice("");
    setBenefits("");
    setMargin("");
    setPlatformPrice("");
    setSelectedPlatform(null);
    onClose();
  };

  const sewasewPlatforms = ["Sewasew Multimedia", "Sewasew SRBT"];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editingSubscription ? "Edit Subscription Tier" : "Manage Subscriptions"}>
      <div className="space-y-6">
        <div className="flex space-x-4 border-b border-slate-200 pb-2">
          <button
            className={`pb-2 font-medium ${activeTab === 'create' ? 'text-primary border-b-2 border-primary' : 'text-slate-500'}`}
            onClick={() => setActiveTab('create')}
          >
            Create Tier
          </button>
          <button
            className={`pb-2 font-medium ${activeTab === 'connect' ? 'text-primary border-b-2 border-primary' : 'text-slate-500'}`}
            onClick={() => setActiveTab('connect')}
          >
            Connect Platform
          </button>
        </div>

        {activeTab === 'create' && (
          <div className="space-y-4">
            <Input
              label="Tier Name"
              placeholder="e.g. Gold Fan"
              value={tierName}
              onChange={(e) => setTierName(e.target.value)}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Monthly Price (ETB)"
                type="number"
                placeholder="0.00"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tier Level</label>
                <select className="w-full p-2 border border-slate-300 rounded-md">
                  <option>Level 1 (Basic)</option>
                  <option>Level 2 (Standard)</option>
                  <option>Level 3 (Premium)</option>
                  <option>Level 4 (VIP)</option>
                  <option>Level 5 (Elite)</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Content Access</label>
              <div className="flex flex-wrap gap-2">
                {["Exclusive Posts", "Behind the Scenes", "Early Access", "All Content"].map((access) => (
                  <label key={access} className="inline-flex items-center space-x-2 bg-slate-50 px-3 py-1 rounded-full border border-slate-200">
                    <input type="checkbox" className="rounded text-primary focus:ring-primary" />
                    <span className="text-sm text-slate-700">{access}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Benefits (comma separated)</label>
              <textarea
                className="w-full p-2 border border-slate-300 rounded-md"
                rows="3"
                placeholder="Exclusive content, Early access, etc."
                value={benefits}
                onChange={(e) => setBenefits(e.target.value)}
              />
            </div>
            <Button onClick={handleSaveTier} className="w-full">
              {editingSubscription ? "Update Tier" : "Create Tier"}
            </Button>
          </div>
        )}

        {activeTab === 'connect' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Select Platform</label>
              <div className="grid grid-cols-1 gap-2">
                {sewasewPlatforms.map((platform) => (
                  <div 
                    key={platform}
                    onClick={() => setSelectedPlatform(platform)}
                    className={`p-3 border rounded-lg cursor-pointer flex justify-between items-center transition-all ${
                      selectedPlatform === platform 
                        ? 'border-primary bg-primary/5 ring-1 ring-primary' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <span className="font-medium text-slate-800">{platform}</span>
                    {selectedPlatform === platform && <div className="h-2 w-2 rounded-full bg-primary"></div>}
                  </div>
                ))}
              </div>
            </div>

            {selectedPlatform && (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 space-y-4 animate-in fade-in slide-in-from-top-2">
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                   <span className="text-sm text-slate-600">Base Fare ({selectedPlatform})</span>
                   <span className="font-bold text-slate-800">{SEWASEW_BASE_MONTHLY} ETB <span className="text-xs font-normal text-slate-500">/ mo</span></span>
                </div>
                
                <Input
                  label="Your Margin (ETB)"
                  type="number"
                  placeholder="0.00"
                  value={margin}
                  onChange={(e) => setMargin(e.target.value)}
                  hint={`Base Price: ${SEWASEW_BASE_MONTHLY} ETB + Margin`}
                />

                <div className="pt-2 flex justify-between items-center">
                  <span className="font-semibold text-slate-700">Total Price to User:</span>
                  <span className="text-xl font-bold text-primary">{platformPrice || "---"} ETB</span>
                </div>
              </div>
            )}

            <Button 
              onClick={handleSaveTier} 
              className="w-full"
              disabled={!selectedPlatform || !margin}
            >
              Connect & Create Subscription
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ManageSubscriptionsModal;
